import { NO } from "uicore-ts"
import { RHCore } from "../../../Core/RHCore"
import { InvitedCompany } from "../../../SocketClientFunctions"


export class RHCPVInviteCountCache {

    // Keyed by `${activeCompanyId}:${cpvCode}`, not bare cpvCode — invited-company
    // data is scoped to whichever company is active, and this cache instance
    // outlives a single company (it lives as long as the owning editor view, and
    // nothing tears it down when the user switches active company mid-edit via the
    // top-bar menu). Without the company in the key, a count/company-list fetched
    // while acting as one company would keep showing under a different, later-active
    // company, exactly the stale-cross-company-data shape this feature's own
    // ProcurementDetailsViewController fix addresses for the procurement details page.
    _companiesByCacheKey = new Map<string, InvitedCompany[]>()
    _invitedCompanyCountsByCacheKey = new Map<string, number>()
    _companyPromisesByCacheKey = new Map<string, Promise<InvitedCompany[]>>()
    _companyReloadPromisesByCacheKey = new Map<string, Promise<InvitedCompany[]>>()


    cachedInvitedCompanyCountForCPVCode(cpvCode: string): number | undefined {
        return this._invitedCompanyCountsByCacheKey.get(this._cacheKeyForCPVCode(cpvCode))
    }


    cachedFoundCompanyCountForCPVCode(cpvCode: string): number | undefined {
        return this._companiesByCacheKey.get(this._cacheKeyForCPVCode(cpvCode))?.length
    }


    async retrieveFoundCompanyCountForCPVCode(cpvCode: string): Promise<number> {
        const cachedCount = this.cachedFoundCompanyCountForCPVCode(cpvCode)
        if (cachedCount !== undefined) {
            return cachedCount
        }

        const companies = await this.retrieveCompaniesForCPVCode(cpvCode)
        return companies.length
    }


    async retrieveCompaniesForCPVCode(cpvCode: string): Promise<InvitedCompany[]> {
        const cacheKey = this._cacheKeyForCPVCode(cpvCode)
        const cachedCompanies = this._companiesByCacheKey.get(cacheKey)
        if (cachedCompanies) {
            return cachedCompanies
        }

        const existingPromise = this._companyPromisesByCacheKey.get(cacheKey)
        if (existingPromise) {
            return existingPromise
        }

        // The promise is cached before its store read yields, coalescing same-tick requests.
        const promise = (async () => {
            const serverCompanies = await RHCore.sharedInstance.procurementStore
                .retrieveInvitedCompaniesForCPVCode(cpvCode)
            // CPV matches are suggestions; only the current procurement may select invitees.
            const companies = serverCompanies.map(company => ({
                ...company,
                isInvited: NO
            }))
            this._companiesByCacheKey.set(cacheKey, companies)
            this._invitedCompanyCountsByCacheKey.set(
                cacheKey,
                this._invitedCompanyCountForCompanies(companies)
            )
            this._companyPromisesByCacheKey.delete(cacheKey)
            return companies
        })().catch(error => {
            this._companyPromisesByCacheKey.delete(cacheKey)
            throw error
        })

        this._companyPromisesByCacheKey.set(cacheKey, promise)
        return promise
    }


    async reloadCompaniesForCPVCode(cpvCode: string): Promise<InvitedCompany[]> {
        const cacheKey = this._cacheKeyForCPVCode(cpvCode)
        const existingReloadPromise = this._companyReloadPromisesByCacheKey.get(cacheKey)
        if (existingReloadPromise) {
            return existingReloadPromise
        }

        const reloadPromise = (async () => {
            const existingRetrievePromise = this._companyPromisesByCacheKey.get(cacheKey)
            if (existingRetrievePromise) {
                try {
                    await existingRetrievePromise
                }
                catch (_error) {
                    // A failed earlier request must not prevent this explicit reload from retrying.
                }
            }

            this._companiesByCacheKey.delete(cacheKey)
            this._invitedCompanyCountsByCacheKey.delete(cacheKey)
            return this.retrieveCompaniesForCPVCode(cpvCode)
        })().finally(() => {
            if (this._companyReloadPromisesByCacheKey.get(cacheKey) === reloadPromise) {
                this._companyReloadPromisesByCacheKey.delete(cacheKey)
            }
        })

        this._companyReloadPromisesByCacheKey.set(cacheKey, reloadPromise)
        return reloadPromise
    }

    replaceCompaniesForCPVCode(cpvCode: string, companies: InvitedCompany[]) {
        const cacheKey = this._cacheKeyForCPVCode(cpvCode)
        this._companiesByCacheKey.set(cacheKey, companies)
        this._invitedCompanyCountsByCacheKey.set(
            cacheKey,
            this._invitedCompanyCountForCompanies(companies)
        )
        this._companyPromisesByCacheKey.delete(cacheKey)
    }


    // Render paths cannot await, so cache partitioning uses the already-loaded company ID.
    _cacheKeyForCPVCode(cpvCode: string): string {
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId ?? "default"
        return `${companyId}:${cpvCode}`
    }


    _invitedCompanyCountForCompanies(companies: InvitedCompany[]): number {
        return companies.filter(company => company.isInvited).length
    }

}
